import { Link } from "react-router-dom";
import { Building2 } from "lucide-react";
import { SITE_CONFIG } from "@/data/siteConfig";
import { usePracticeStats } from "@/hooks/usePracticeStats";

const FOOTER_NAV = [
  { label: "Home", href: "/" },
  { label: "Search", href: "/search" },
  { label: "Blog", href: "/blog" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
];

const FOOTER_LEGAL = [
  { label: "Privacy Policy", href: "/privacy" },
  { label: "Cookie Policy", href: "/cookie-policy" },
  { label: "Disclaimers", href: "/disclaimers" },
  { label: "FAQ", href: "/faq" },
];

const Footer = () => {
  const { data: stats } = usePracticeStats();
  const total = stats?.total ?? 0;

  return (
  <footer className="border-t bg-card">
    {/* Provider banner */}
    <div className="bg-primary/5 px-4 py-3 text-center">
      <p className="text-sm text-muted-foreground">
        <span className="font-semibold text-primary">Healthcare providers:</span>{" "}
        <Link to="/claim" className="underline hover:text-primary">
          Claim your listing
        </Link>{" "}
        to enhance your visibility and keep your details up to date.
      </p>
    </div>

    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="flex flex-col items-center gap-6 text-center md:flex-row md:justify-between md:text-left">
        <div>
          <p className="text-lg font-bold text-primary" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {SITE_CONFIG.name}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            Helping patients find the right healthcare provider.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
          {FOOTER_NAV.map((item) => (
            <Link
              key={item.label}
              to={item.href}
              className="text-sm text-muted-foreground transition-colors hover:text-primary"
            >
              {item.label}
            </Link>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="mt-8 flex flex-col items-center justify-center gap-2 rounded-lg bg-accent/10 px-4 py-4 sm:flex-row sm:gap-6">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Building2 className="h-4 w-4 text-primary" />
          <span>Over <span className="font-bold text-primary">{total.toLocaleString("en-GB")}</span> healthcare practices listed</span>
        </div>
      </div>

      <div className="mt-8 border-t pt-6 text-center text-xs text-muted-foreground">
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
          {FOOTER_LEGAL.map((item, i) => (
            <span key={item.label} className="flex items-center gap-4">
              {i > 0 && <span className="text-border">|</span>}
              <Link to={item.href} className="hover:text-primary">
                {item.label}
              </Link>
            </span>
          ))}
        </div>
        <p className="mt-4">{SITE_CONFIG.copyright}</p>
        <p className="mt-1">
          Contact:{" "}
          <a href={`mailto:${SITE_CONFIG.contactEmail}`} className="text-primary hover:underline">
            {SITE_CONFIG.contactEmail}
          </a>
        </p>
      </div>
    </div>

    {/* Legal registration line */}
    <div className="border-t border-border/50">
      <div className="mx-auto max-w-7xl px-4 py-4 text-center">
        <p className="text-xs text-muted-foreground/70">
          My Health Directory is a product of Axxton Limited. Registered in England &amp; Wales. Company No: 17112701. ICO Registration: ZC110166.
        </p>
      </div>
    </div>
  </footer>
  );
};

export default Footer;
